import { SearchBoxProps } from '@mapbox/search-js-react/dist/components/SearchBox';
import { Box, Link } from '@mui/material';
import { ReactComponent as Pin } from 'assets/img/icons/pin.svg';
import SelectableMap from 'Components/Forms/SelectableMap';
import { COUNTRIES } from 'utils/countries.utils';
import { ThemeTypography } from 'DesignSystem';
import CoordinatesInput from 'DesignSystem/Formik/CoordinatesInputField/CoordinatesInputField';
import GeoLocationAutocompleteField from 'DesignSystem/Formik/GeoLocationAutocompleteField/GeoLocationAutocompleteField';
import { Field, useField, useFormikContext } from 'formik';
import React, { FC, useMemo } from 'react';
import { themeColors } from 'styles/themeColors';
import { ICoordinates } from 'types/map.types';

interface ILocationSelectorBlockProps {
  showOnlyCountry?: boolean;
  defaultCountry?: string;
}

const LocationSelectorBlock: FC<ILocationSelectorBlockProps> = ({
  showOnlyCountry,
  defaultCountry,
}) => {
  const [{ value: useExactCoordinates }, , { setValue: setUseExactCoordinates }] = useField<
    boolean | null
  >('useExactCoordinates');
  const formikContext = useFormikContext();

  const [, , { setValue: setLocationCoordinates }] = useField<ICoordinates | null>(
    'locationCoordinates'
  );
  const [, , { setValue: setMapBoxId }] = useField<string | null>('mapboxId');

  const language = formikContext?.status?.lang;

  const handleGeocodingRetrieve: SearchBoxProps['onRetrieve'] = response => {
    if (response.features.length > 0) {
      setMapBoxId(response.features[0].properties.mapbox_id);
      const coordinates = response.features[0].geometry.coordinates;
      setLocationCoordinates({ lat: coordinates[1], lng: coordinates[0] });
    }
  };

  const defaultCountryCode = useMemo(() => {
    if (!defaultCountry) return undefined;

    return COUNTRIES.find(
      country => country.label.toUpperCase() === defaultCountry?.toLocaleUpperCase()
    )?.code;
  }, [defaultCountry]);

  return (
    <Box>
      <Box display="flex" alignItems="end" mb={0.5}>
        <ThemeTypography variant="BODY_MEDIUM_BOLD">
          {showOnlyCountry ? 'Country' : 'Location'}
          <span style={{ color: themeColors.primary[40] }}>&nbsp;*</span>&nbsp;&nbsp;
        </ThemeTypography>
        {!showOnlyCountry &&
          (useExactCoordinates ? (
            <>
              <ThemeTypography variant="BODY_SMALL">
                Can&apos;t get the right location?&nbsp;
              </ThemeTypography>
              <ThemeTypography variant="BODY_SMALL_BOLD">
                <Link onClick={() => setUseExactCoordinates(false)}>Search address instead</Link>
              </ThemeTypography>
            </>
          ) : (
            <>
              <ThemeTypography variant="BODY_SMALL">
                Can&apos;t find the location? &nbsp;
              </ThemeTypography>
              <ThemeTypography variant="BODY_SMALL_BOLD">
                <Link onClick={() => setUseExactCoordinates(true)}>
                  Select on the map or add coordinates
                </Link>
              </ThemeTypography>
            </>
          ))}
      </Box>
      <Box>
        {useExactCoordinates ? (
          <Box>
            <Box display="flex" gap={1} alignItems="center" width="100%" mb={1.5} mt={0.5}>
              <Pin width={16} />
              <Field
                component={CoordinatesInput}
                name="locationCoordinates"
                locationNameKey="locationName"
              />
            </Box>
            <Box height={300}>
              <Field
                component={SelectableMap}
                name="locationCoordinates"
                locationNameKey="locationName"
              />
            </Box>
          </Box>
        ) : (
          <Box display="flex" gap={1} alignItems="center" maxHeight={30}>
            <Pin width={16} />
            <Field
              fullWidth
              component={GeoLocationAutocompleteField}
              name="locationName"
              size="small"
              language={language}
              variant="outlined"
              searchTypes={showOnlyCountry ? 'country' : undefined}
              country={defaultCountryCode ? defaultCountryCode : undefined}
              onRetrieve={handleGeocodingRetrieve}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LocationSelectorBlock;
